import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDeleteTournament, useMatches, useTournament, useUploadBackground } from '../api/hooks';
import ErrorBanner from '../components/ErrorBanner';
import MatchEditor from '../components/MatchEditor';
import PosterGallery from '../components/PosterGallery';

type Tab = 'posters' | 'fixtures' | 'settings';

export default function TournamentDetailPage() {
  const { tournamentId = '' } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('posters');
  // Reported from the loaded image so the page can confirm what was actually stored.
  const [backgroundSize, setBackgroundSize] = useState<{ width: number; height: number } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const tournament = useTournament(tournamentId);
  const matches = useMatches(tournamentId);
  const uploadBackground = useUploadBackground(tournamentId);
  const deleteTournament = useDeleteTournament();

  if (tournament.isLoading) {
    return <p className="muted">Loading competition...</p>;
  }

  if (tournament.error || !tournament.data) {
    return <ErrorBanner error={tournament.error ?? new Error('Competition not found.')} />;
  }

  const data = tournament.data;

  return (
    <div className="stack">
      <nav className="breadcrumb">
        <Link to="/">Competitions</Link> <span aria-hidden="true">/</span> {data.name}
      </nav>

      <div className="page-head">
        <div>
          <h1>{data.name}</h1>
          <p className="muted">
            {data.season} &middot; {data.weeks} week{data.weeks === 1 ? '' : 's'} &middot;{' '}
            {new Date(data.startDate).toLocaleDateString('en-GB')} to{' '}
            {new Date(data.endDate).toLocaleDateString('en-GB')}
          </p>
        </div>
        <div className="swatches">
          <div className="swatch" style={{ background: data.primaryColour }} title={data.primaryColour} />
          <div className="swatch" style={{ background: data.accentColour }} title={data.accentColour} />
        </div>
      </div>

      <div className="tabs" role="tablist">
        {(['posters', 'fixtures', 'settings'] as Tab[]).map((value) => (
          <button
            key={value}
            role="tab"
            type="button"
            aria-selected={tab === value}
            className={`tabs__tab ${tab === value ? 'is-active' : ''}`}
            onClick={() => setTab(value)}
          >
            {value === 'posters' ? 'Posters' : value === 'fixtures' ? 'Fixtures' : 'Background & settings'}
          </button>
        ))}
      </div>

      {tab === 'posters' && (
        <PosterGallery
          tournament={data}
          matches={matches.data ?? []}
          onGoToFixtures={() => setTab('fixtures')}
        />
      )}

      {tab === 'fixtures' && (
        <MatchEditor
          tournament={data}
          matches={matches.data ?? []}
          isLoading={matches.isLoading}
          onPosterQueued={() => setTab('posters')}
        />
      )}

      {tab === 'settings' && (
        <div className="stack">
          <section className="card">
            <h2>Core background image</h2>
            <p className="muted">
              Every poster for this competition starts from this image. The image model is asked to keep
              its palette and mood, so the whole series looks like a set. Without one, the club-wide
              default background is used.
            </p>

            {data.backgroundUrl ? (
              <figure className="background-preview">
                <div className="background-preview__frame">
                  <img
                    src={data.backgroundUrl}
                    alt="Core background"
                    onLoad={(e) =>
                      setBackgroundSize({
                        width: e.currentTarget.naturalWidth,
                        height: e.currentTarget.naturalHeight,
                      })
                    }
                  />
                </div>
                <figcaption className="muted small">
                  {backgroundSize
                    ? `Whole image shown, ${backgroundSize.width} x ${backgroundSize.height} pixels. `
                    : ''}
                  <a href={data.backgroundUrl} target="_blank" rel="noreferrer">
                    Open full size
                  </a>
                </figcaption>
              </figure>
            ) : (
              <p className="muted small">No background uploaded yet.</p>
            )}

            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  void uploadBackground.mutateAsync(file);
                }
                e.target.value = '';
              }}
            />

            <ErrorBanner error={uploadBackground.error} />

            <button
              type="button"
              className="button button--primary"
              disabled={uploadBackground.isPending}
              onClick={() => fileInput.current?.click()}
            >
              {uploadBackground.isPending ? 'Uploading...' : 'Upload background'}
            </button>
          </section>

          <section className="card">
            <h2>Danger zone</h2>
            <p className="muted">
              Deleting a competition also deletes its fixtures and every poster generated for it.
            </p>
            <ErrorBanner error={deleteTournament.error} />
            <button
              type="button"
              className="button button--danger"
              onClick={async () => {
                if (window.confirm(`Delete "${data.name}" and everything in it?`)) {
                  await deleteTournament.mutateAsync(data.id);
                  void navigate('/');
                }
              }}
            >
              Delete competition
            </button>
          </section>
        </div>
      )}
    </div>
  );
}
